import React, { ReactNode } from "react";
import { Formik, Form, FormikValues, FormikHelpers, FormikProps } from "formik";
import Button from "../Button";

interface ButtonProps {
  isHide?: boolean;
  children?: ReactNode;
  className?: string;
  isDisabled?: boolean;
}

interface FormikWrapperProps {
  initialValues: FormikValues;
  validationSchema?: any;
  validate?: (values: FormikValues) => void | object | Promise<any>;
  onSubmit: (
    values: FormikValues,
    formikHelpers: FormikHelpers<FormikValues>
  ) => void | Promise<any>;
  children: ReactNode | any;
  buttonProps?: ButtonProps;
  innerRef?: any;
}

const FormikWrapper: React.FC<FormikWrapperProps> = ({
  initialValues,
  validationSchema,
  validate,
  onSubmit,
  children,
  innerRef,
  buttonProps = {
    isHide: false,
    children: "Submit",
    className: "",
    isDisabled: false,
  },
}) => {
  const focusFirstErrorField = (formik: FormikProps<FormikValues>) => {
    if (formik.errors && formik.touched) {
      const firstErrorField = formik.errors
        ? Object.keys(formik.errors).find((key) => formik.touched[key])
        : "";

      if (firstErrorField) {
        if (Array.isArray(formik.errors[firstErrorField])) {
          const errorsArray = formik.errors[firstErrorField] as Array<any>; // Assert as array
          const index = errorsArray.findIndex(
            (item) => item !== null && item !== undefined
          );

          if (index !== -1) {
            const errorItem = errorsArray[index]; // Access the item at the index
            if (errorItem && typeof errorItem === "object") {
              const keys = Object.keys(errorItem); // Get keys of the error item
              if (keys.length > 0) {
                const errorFieldName = `${firstErrorField}.${index}.${keys[0]}`; // Construct the field name
                const errorElement =
                  document.querySelectorAll(
                    `[data-name='${errorFieldName}']`
                  )[0] || document.getElementsByName(errorFieldName)[0];

                if (errorElement) {
                  (errorElement as any)?.scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                    block: "center",
                  });
                  (errorElement as any)?.focus();
                }
              }
            }
          }
        } else {
          const errorElement =
            document.querySelectorAll(`[data-name='${firstErrorField}']`)[0] ||
            document.getElementsByName(firstErrorField)[0];

          if (errorElement) {
            (errorElement as any)?.focus();
          }
        }
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validate={validate}
      onSubmit={onSubmit}
      innerRef={innerRef}
    >
      {(formik: FormikProps<FormikValues>) => (
        <Form onSubmit={formik.handleSubmit} className="w-full">
          <>
            {children}
            {!buttonProps?.isHide && (
              <Button
                type="submit"
                className={buttonProps.className}
                disabled={buttonProps.isDisabled}
              >
                {buttonProps?.children}
              </Button>
            )}
            {formik?.isSubmitting && focusFirstErrorField(formik)}
          </>
        </Form>
      )}
    </Formik>
  );
};

export default FormikWrapper;
